import { assertKitchenResponse } from "./kitchenActions";
import { parseReceivedTime } from "./orderTiming";

const message = "Unable to verify the aggregated kitchen workload. Please retry.";
const envelopeOf = (response) => {
  const candidates = [response?.data, response?.data?.data, response?.data?.data?.data, response]
    .filter((value) => value && typeof value === "object" && !Array.isArray(value));
  if (Number(response?.status) >= 400 || candidates.some((value) => Number(value.statusCode) >= 400)) {
    throw new Error(message);
  }
  const errors = candidates.filter((value) => value.es > 0 || value.success === false ||
    ["ERROR", "FAILED", "FAILURE"].includes(value.status));
  // A read-only status query may report an empty queue as a business "No data found".
  // Only that exact empty response is benign; authentication/server failures still fail.
  const empty = candidates.find((value) => /^no\s+data\s+found[.!]?$/i.test(String(value.message || "").trim()) &&
    (value.data == null || (Array.isArray(value.data) && !value.data.length)) &&
    (value.totalResults == null || Number(value.totalResults) === 0));
  if (empty && errors.every((value) => value === empty)) {
    return { ...empty, es: 0, data: [], totalPages: 0, currentPage: null };
  }
  assertKitchenResponse(response);
  if (errors.length) throw new Error(errors[0].message || message);
  const data = candidates.find((value) => (
    Array.isArray(value.data) && value.totalPages != null
  ) || (
    (value.data == null || (Array.isArray(value.data) && !value.data.length)) &&
    (value.totalPages != null && Number(value.totalPages) === 0 || value.totalResults != null && Number(value.totalResults) === 0)
  ));
  if (!data) throw new Error(message);
  if (data.data == null && Number(data.totalResults || 0) > 0) throw new Error(message);
  return { ...data, data: data.data || [], totalPages: data.totalPages ?? 0 };
};
// Add-ons belong to the order line, so prefer the per-order detail over the dish group.
const addonsOf = (detail, item) => [
  detail?.addonProducts, detail?.addons, detail?.additionalProperties?.addons, detail?.metadata?.addons,
  item?.addonProducts, item?.addons,
].find((value) => Array.isArray(value) && value.length) || [];
const earlier = (left, right) => {
  const a = parseReceivedTime(left), b = parseReceivedTime(right);
  return a == null ? right : b == null || a <= b ? left : right;
};

/** Adapt the API's real task details to the existing views and action contracts. */
export const mapAggregatedKitchenJobs = (rows, aggregationType) => {
  const jobs = new Map();
  rows.forEach((row) => {
    // Accept flat item groups and table-wrapped item groups in either view.
    const items = Array.isArray(row?.items) ? row.items : [row];
    if (!Array.isArray(items)) throw new Error(message);
    if (!items.length && Number(row?.totalQuantity) > 0) throw new Error(message);
    items.forEach((item) => {
      if (!Array.isArray(item?.subTaskDetails)) throw new Error(message);
      if (!item.subTaskDetails.length && Number(item?.totalQuantity) > 0) throw new Error(message);
      item.subTaskDetails.forEach((detail) => {
        if (!detail?.jobId || !detail?.taskId || detail?.subTaskQuantity == null ||
          !Number.isFinite(Number(detail?.subTaskQuantity)) || Number(detail.subTaskQuantity) < 0) {
          throw new Error(message);
        }
        const status = detail?.taskStatus || item?.taskStatus;
        if (!["PENDING", "IN_PROGRESS"].includes(status) || Number(detail.subTaskQuantity) === 0) return;
        const metadata = {
          ...(detail?.metadata || {}),
          tableId: detail?.tableId ?? row?.tableId,
          tableNumber: detail?.tableNumber ?? row?.tableNumber,
          layoutId: detail?.layoutId ?? row?.layoutId,
          layoutName: detail?.layoutName ?? row?.layoutName,
          subOrderType: detail?.orderType,
          userName: detail?.userName ?? "",
          captainName: detail?.captainName,
          cartId: detail?.cartId ?? detail?.metadata?.cartId,
        };
        const job = jobs.get(String(detail.jobId)) || {
          aggregated: true,
          jobId: detail.jobId, token: detail?.jobToken ?? detail?.tokenNumber,
          source: detail?.jobSource, type: detail?.orderType,
          externalRefId: detail?.externalRefId, orderId: detail?.orderId,
          orderSequence: detail?.orderSequence,
          created: detail?.createdAt, status: "PENDING", metadata, tasks: [],
        };
        const task = {
          taskId: detail.taskId, jobId: detail.jobId,
          itemId: detail?.subTaskVariantId ?? item?.variantId,
          externalId: detail?.externalId,
          title: item?.title ?? detail?.subTaskVariantName,
          quantity: Number(detail.subTaskQuantity), status, taskStatus: status,
          created: detail?.createdAt,
          earliestCreatedAt: item?.earliestCreatedAt,
          prepTime: item?.prepTime, aggregationPrepTime: item?.prepTime,
          metadata: {
            ...(detail?.metadata || {}),
            variantId: detail?.subTaskVariantId ?? item?.variantId,
            variantName: detail?.subTaskVariantName ?? item?.variantName,
            comments: detail?.comments ?? "",
            prepStationId: detail?.prepStationId ?? item?.prepStationId,
            prepStationName: detail?.prepStationName ?? item?.prepStationName,
            kitchenId: detail?.kitchenId ?? item?.kitchenId,
            kitchenName: detail?.kitchenName ?? item?.kitchenName,
            foodType: item?.foodType, placedSequence: detail?.orderSequence,
            cartProductId: detail?.cartProductId ?? detail?.metadata?.cartProductId,
          },
          additionalProperties: { addons: addonsOf(detail, item) },
        };
        const existing = job.tasks.findIndex((value) => String(value.taskId) === String(task.taskId));
        // Processing wins if a task changes state between the two status reads.
        if (existing < 0) job.tasks.push(task);
        else if (job.tasks[existing].status !== "IN_PROGRESS") job.tasks[existing] = task;
        job.created = earlier(job.created, detail?.createdAt);
        if (job.tasks.some((value) => value.status === "IN_PROGRESS")) job.status = "IN_PROGRESS";
        const prepTimes = job.tasks.map((value) => Number(value.prepTime))
          .filter((value, index) => job.tasks[index].prepTime != null && Number.isFinite(value) && value >= 0);
        // Keep the order's estimate at least as long as its longest live dish.
        job.metadata.avgPrepTime = prepTimes.length ? Math.max(...prepTimes) : null;
        jobs.set(String(job.jobId), job);
      });
    });
  });
  return [...jobs.values()];
};

/** Read every page per task state before publishing a complete snapshot. */
export const loadAggregatedKitchenWorkload = async (
  fetchAggregates, filters, aggregationType, statuses = ["PENDING", "IN_PROGRESS"],
  isCurrent = () => true, pageSize = 10,
) => {
  if (!["ITEM", "TABLE"].includes(aggregationType)) throw new Error(message);
  let failed = false;
  const current = () => !failed && isCurrent();
  const loadStatus = async (status) => {
    const rows = [], seenPages = new Set();
    let totalPages = 1;
    for (let page = 1; page <= totalPages; page += 1) {
      if (!current()) return null;
      const response = await fetchAggregates({
        ...filters, aggregationType, taskStatuses: [status], page, pageSize,
      });
      if (!current()) return null;
      const data = envelopeOf(response);
      if (page > 1 && !data.data.length) throw new Error(message);
      const count = Number(data?.totalPages);
      if (!Number.isInteger(count) || count < 0 || count > 1000 ||
        (count === 0 && data.data.length) ||
        (data?.currentPage != null && Number(data.currentPage) !== page &&
          !(count === 0 && !data.data.length && Number(data.currentPage) === 0))) throw new Error(message);
      totalPages = count;
      const signature = JSON.stringify(data.data);
      if ((page < totalPages && !data.data.length) ||
        (data.data.length && seenPages.has(signature))) throw new Error(message);
      seenPages.add(signature);
      rows.push(...data.data);
    }
    return rows;
  };
  const results = await Promise.all(statuses.map((status) => loadStatus(status).catch((error) => {
    failed = true;
    throw error;
  })));
  if (!isCurrent() || results.some((value) => value == null)) return null;
  return mapAggregatedKitchenJobs(results.flat(), aggregationType);
};
